import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

import { ParameterProxyService } from '../../../proxies/item/parameter.proxy.service';
import { Parameter } from '../../../models/engineering/parameter';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues, ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { ParameterDetailsModalComponent } from '../../modals/parameter-details-modal/parameter-details-modal.component';
import { ProductDevelopmentAdminGroup, ProductDevelopmentViewerGroup } from '../../../models/authorization/enum/user-group.enum';
import { EnumService } from '../../../services/enum.service';

@Component({
  selector: 'app-parameters',
  templateUrl: './parameters.component.html',
  styleUrl: './parameters.component.scss',
})
export class ParametersComponent implements OnInit {
  private parameterProxyService = inject(ParameterProxyService);
  private modalService = inject(ModalService);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'parameters-filters', config: this.filterConfig });
  showFilters = signal<boolean>(true);
  parameters = signal<Parameter[]>([]);
  private nameFilter = signal<string>('');

  // Filtered on the client: the API returns every parameter in one call. A computed keeps the
  // applied term in effect when the list reloads after saving from the modal.
  filteredParameters = computed(() => {
    const term = this.nameFilter().trim().toLowerCase();
    if (!term) return this.parameters();
    return this.parameters().filter(
      (parameter) => parameter.name.en.toLowerCase().includes(term) || parameter.name.es.toLowerCase().includes(term),
    );
  });

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.initializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  parameterTypes: EnumValues = {};

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.applyNameFilter(filters));
  }

  private updateLoadingState(isLoading: boolean): void {
    this.isLoading.update((value) => (isLoading ? value + 1 : value - 1));
  }

  getRowData(rowData: unknown): Parameter {
    return rowData as Parameter;
  }

  getParameterTypeName(parameterType: number): string {
    return this.parameterTypes[parameterType] ?? 'Unknown';
  }

  canEdit() {
    // if Admin
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.ProcessParametersAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.ProcessParametersViewer)) {
      return false;
    }
    // Default
    return false;
  }

  ngOnInit() {
    this.parameterTypes = this.enumService.getEnum('ParameterType', 'itemEnums').values;

    this.updateLoadingState(true);
    this.loadParameters();
  }

  private loadParameters() {
    this.parameterProxyService
      .getParameters()
      .pipe(finalize(() => this.updateLoadingState(false)))
      .subscribe({
        next: (parameters) => {
          this.parameters.set(parameters.data || []);
        },
      });
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    this.filterConfig.set({
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'name',
          label: translate('Parameter_Name', 'Name'),
          placeholder: translate('Parameter_Enter_Name', 'Enter parameter name'),
          defaultVisible: true,
        },
      ],
    });
  }

  private applyNameFilter(filters: DynamicFilterValues): void {
    this.nameFilter.set(typeof filters['name'] === 'string' ? filters['name'] : '');
  }

  openParamModal(param?: Parameter) {
    this.modalService
      .open(
        ParameterDetailsModalComponent,
        { modalClass: 'modal_small' },
        {
          parameter: param,
          canEdit: this.canEdit(),
        },
      )
      .subscribe({
        next: (result) => {
          console.log(result);
          if (result && result.action === 'save' && result.data) {
            this.loadParameters();
          }
        },
      });
  }
}
